import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type UIState = {
  darkMode: boolean;
  toggleDark: () => void;
  setDark: (v: boolean) => void;

  weekOffset: number; // 0 = this week, -1 prev, +1 next
  setWeekOffset: (v: number) => void;
  weekNext: () => void;
  weekPrev: () => void;
  weekToday: () => void;

  view: 'week' | 'day' | 'month';
  setView: (v: UIState['view']) => void;

  viewMode: 'plan' | 'actual' | 'hybrid';
  setViewMode: (v: UIState['viewMode']) => void;

  dayOffset: number;
  setDayOffset: (v: number) => void;
  goPrevDay: () => void;
  goNextDay: () => void;
  goToday: () => void;

  monthOffset: number;
  setMonthOffset: (v: number) => void;
  goPrevMonth: () => void;
  goNextMonth: () => void;
  goThisMonth: () => void;

  sidebarWidth: number;
  setSidebarWidth: (px: number) => void;

  mobilePanel: 'todos' | 'calendar';
  setMobilePanel: (v: 'todos' | 'calendar') => void;
  toggleMobilePanel: () => void;

  sort: 'score' | 'due' | 'manual';
  setSort: (v: UIState['sort']) => void;

  tagOrder: string[];
  setTagOrder: (v: string[]) => void;

  groupByTag: boolean;
  toggleGroupByTag: () => void;
  setGroupByTag: (v: boolean) => void;

  taskFilterMode: 'off' | 'minScore' | 'topN';
  taskFilterMin: number;
  taskFilterTopN: number;
  setTaskFilterMode: (v: UIState['taskFilterMode']) => void;
  setTaskFilterMin: (n: number) => void;
  setTaskFilterTopN: (n: number) => void;

  showDone: boolean;
  toggleShowDone: () => void;
  setShowDone: (v: boolean) => void;

  showChunksAll: boolean;
  toggleShowChunksAll: () => void;
  setShowChunksAll: (v: boolean) => void;

  zoomLevel: number;
  setZoomLevel: (v: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomReset: () => void;

  quietHoursEnabled: boolean;
  quietHoursFrom: number;
  quietHoursTo: number;
  toggleQuietHours: () => void;
  setQuietHours: (patch: Partial<{ enabled: boolean; from: number; to: number }>) => void;

  appColorOverrides: Record<string, string>; // label → tone id (see app-colors.ts)
  setAppColorOverride: (label: string, toneId: string) => void;
  resetAppColor: (label: string) => void;

  modal: null | 'briefing' | 'overview' | 'settings' | 'taskDetail' | 'blockDetail' | 'export';
  modalTaskId: string | null;
  modalBlockId: string | null;
  openModal: (m: UIState['modal'], id?: string) => void;
  closeModal: () => void;

  toast: {
    msg: string;
    tone: 'accent' | 'ok' | 'bad';
    actionLabel?: string;
    onAction?: () => void;
  } | null;
  setToast: (t: UIState['toast']) => void;
};

export const useUI = create<UIState>()(
  persist(
    (set) => ({
      darkMode: false,
      toggleDark: () => set((s) => ({ darkMode: !s.darkMode })),
      setDark: (v) => set({ darkMode: v }),

      weekOffset: 0,
      setWeekOffset: (v) => set({ weekOffset: v }),
      weekNext: () => set((s) => ({ weekOffset: s.weekOffset + 1 })),
      weekPrev: () => set((s) => ({ weekOffset: s.weekOffset - 1 })),
      weekToday: () => set({ weekOffset: 0 }),

      view: 'week',
      setView: (v) => set({ view: v }),

      viewMode: 'plan',
      setViewMode: (v) => set({ viewMode: v }),

      dayOffset: 0,
      setDayOffset: (v) => set({ dayOffset: v }),
      goPrevDay: () => set((s) => ({ dayOffset: s.dayOffset - 1 })),
      goNextDay: () => set((s) => ({ dayOffset: s.dayOffset + 1 })),
      goToday: () => set({ dayOffset: 0 }),

      monthOffset: 0,
      setMonthOffset: (v) => set({ monthOffset: v }),
      goPrevMonth: () => set((s) => ({ monthOffset: s.monthOffset - 1 })),
      goNextMonth: () => set((s) => ({ monthOffset: s.monthOffset + 1 })),
      goThisMonth: () => set({ monthOffset: 0 }),

      sidebarWidth: 320,
      setSidebarWidth: (px) => set({ sidebarWidth: Math.max(240, Math.min(560, Math.round(px))) }),

      mobilePanel: 'todos',
      setMobilePanel: (v) => set({ mobilePanel: v }),
      toggleMobilePanel: () => set((s) => ({ mobilePanel: s.mobilePanel === 'todos' ? 'calendar' : 'todos' })),

      sort: 'score',
      setSort: (v) => set({ sort: v }),

      tagOrder: [],
      setTagOrder: (v) => set({ tagOrder: v }),

      groupByTag: true,
      toggleGroupByTag: () => set((s) => ({ groupByTag: !s.groupByTag })),
      setGroupByTag: (v) => set({ groupByTag: v }),

      taskFilterMode: 'off',
      taskFilterMin: 50,
      taskFilterTopN: 5,
      setTaskFilterMode: (v) => set({ taskFilterMode: v }),
      setTaskFilterMin: (n) => set({ taskFilterMin: Math.max(0, Math.min(100, Math.round(n))) }),
      setTaskFilterTopN: (n) => set({ taskFilterTopN: Math.max(1, Math.min(99, Math.round(n))) }),

      showDone: true,
      toggleShowDone: () => set((s) => ({ showDone: !s.showDone })),
      setShowDone: (v) => set({ showDone: v }),

      showChunksAll: false,
      toggleShowChunksAll: () => set((s) => ({ showChunksAll: !s.showChunksAll })),
      setShowChunksAll: (v) => set({ showChunksAll: v }),

      zoomLevel: 1,
      setZoomLevel: (v) => set({ zoomLevel: clampZoom(v) }),
      zoomIn: () => set((s) => ({ zoomLevel: clampZoom(s.zoomLevel + 0.25) })),
      zoomOut: () => set((s) => ({ zoomLevel: clampZoom(s.zoomLevel - 0.25) })),
      zoomReset: () => set({ zoomLevel: 1 }),

      quietHoursEnabled: false,
      quietHoursFrom: 22,
      quietHoursTo: 6,
      toggleQuietHours: () => set((s) => ({ quietHoursEnabled: !s.quietHoursEnabled })),
      setQuietHours: (patch) =>
        set((s) => ({
          quietHoursEnabled: patch.enabled ?? s.quietHoursEnabled,
          quietHoursFrom:
            patch.from != null ? Math.max(0, Math.min(24, patch.from)) : s.quietHoursFrom,
          quietHoursTo:
            patch.to != null ? Math.max(0, Math.min(24, patch.to)) : s.quietHoursTo,
        })),

      appColorOverrides: {},
      setAppColorOverride: (label, toneId) =>
        set((s) => ({ appColorOverrides: { ...s.appColorOverrides, [label]: toneId } })),
      resetAppColor: (label) =>
        set((s) => {
          const next = { ...s.appColorOverrides };
          delete next[label];
          return { appColorOverrides: next };
        }),

      modal: null,
      modalTaskId: null,
      modalBlockId: null,
      openModal: (m, id) =>
        set({
          modal: m,
          modalTaskId: m === 'taskDetail' ? id ?? null : null,
          modalBlockId: m === 'blockDetail' ? id ?? null : null,
        }),
      closeModal: () => set({ modal: null, modalTaskId: null, modalBlockId: null }),

      toast: null,
      setToast: (t) => set({ toast: t }),
    }),
    { name: 'focuscoach.ui.v1' },
  ),
);

function clampZoom(v: number) {
  // Snap to 25 % raster (100 → 125 → 150 → …), range 50 – 300 %.
  const snapped = Math.round(v * 4) / 4;
  return Math.max(0.5, Math.min(3, snapped));
}

export function visibleHourRange(s: Pick<UIState, 'quietHoursEnabled' | 'quietHoursFrom' | 'quietHoursTo'>): { startHour: number; endHour: number } {
  if (!s.quietHoursEnabled) return { startHour: 0, endHour: 24 };
  let start = s.quietHoursTo;
  let end = s.quietHoursFrom;
  if (end <= start) end = start + 1;
  return { startHour: Math.max(0, Math.min(23.75, start)), endHour: Math.max(start + 1, Math.min(24, end)) };
}
